import { useForm, useController } from "react-hook-form";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import useAuth from "./hooks/useAuth";
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";
import Select from 'react-select';
import Captcha from "./Components/Captcha";

const getCharacterValidationError = (string) => {
    return `Your password must have at least 1 ${string} character`;
};

const schema = yup.object({
    username: yup.string().required("Please enter username"),
    email: yup.string().email("Please enter valid email").required("Please enter email"),
    password: yup.string()
        .required("Please enter a password")
        .min(8, "Password must have at least 8 characters")
        .matches(/[0-9]/, getCharacterValidationError("digit"))
        .matches(/[a-z]/, getCharacterValidationError("lowercase"))
        .matches(/[A-Z]/, getCharacterValidationError("uppercase")),
    confirmPassword: yup.string()
        .required("Please re-type your password")
        .oneOf([yup.ref("password")], "Passwords do not match"),
    role: yup.string().nullable().required("Please select role"),
    gender: yup.string().nullable().optional(),
    age: yup.string().nullable().optional()
});

const rolesList = [
  { value: 'USER', label: 'User' },
  { value: 'ADMIN', label: 'Admin' },
  { value: 'CREATOR', label: 'Creator' }
];

const Register = ({successHandler}) => {
    const [apiError, setApiError] = useState();
    const [captchaVerifed, setCaptcha] = useState(false);
    const { setAuth } = useAuth();
    const [emailError, setEmailError] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const verifyCaptcha = () => { setCaptcha(true); }

    let navigate = useNavigate();
    const {
      register,
      handleSubmit,
      formState: { errors }, control
    } = useForm({
        resolver: yupResolver(schema)
    });

    const { field: { value: langValue, onChange: langOnChange, ...restLangField } } = useController({ name: 'role', control });

    const getUserData = async (token) => {
        const response = await fetch(`${process.env.REACT_APP_USER_API_URL}/users/me`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        if (response.ok) {
          let userData = await response.json();
          userData["token"] = token;
          setAuth(userData);
          localStorage.setItem("token", token);
          setApiError(null);
          if (successHandler) {
            successHandler(false);
          } else {
            navigate("/");
          }
        }
    };
   
    const onFormSubmit = async (data) => {
        setIsLoading(true);
        setEmailError("");
        setApiError(null);
        
        try {
            if (!isNaN(data["age"])) {
              data["age"] = Number(data["age"]);
            }
            const response = await fetch(`${process.env.REACT_APP_USER_API_URL}/users/register`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify(data),
            });
            
            if (response.ok) {
                const response_login = await fetch(`${process.env.REACT_APP_USER_API_URL}/users/login`, {
                    method: "POST",
                    headers: {
                      "Content-Type": "application/json",
                    },
                    body: JSON.stringify({email: data.email, password: data.password}),
                });
                const token = await response_login.json();
                await getUserData(token["token"]);
            } else if (response.status === 422) {    
              setEmailError("Not valid email");
            } else {
              let errorResponse = await response.json();
              setApiError(errorResponse["detail"]);
              setAuth(null);
            }
        } catch (error) {
            setApiError("Registration failed. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };
    
    const onErrors = (errors) => console.error(errors);

    return (
      <div className="w-full space-y-4">
        {/* Header */}
        <div className="text-center">
          <h2 className="text-xl font-bold text-gray-800 mb-2">Create Your Account</h2>
          <p className="text-sm text-gray-600">Join The Catchphraser</p>
        </div>

        {/* Back Button */}
        <button 
          onClick={() => { successHandler(false) }}
          className="flex items-center text-blue-600 hover:text-blue-800 text-sm font-medium transition-colors duration-200"
        >
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Login
        </button>

        {!captchaVerifed && <Captcha func={verifyCaptcha} />}
        
        {captchaVerifed && (
          <div className="space-y-4">
            <form onSubmit={handleSubmit(onFormSubmit, onErrors)} className="space-y-3">
              {/* Username Field */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Username
                </label>
                <input
                  type="text"
                  placeholder="Enter your username"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 text-sm"
                  autoComplete="username"
                  {...register("username")}
                />
                {errors?.username && (
                  <p className="mt-1 text-xs text-red-600">{errors.username.message}</p>
                )}
              </div>

              {/* Email Field */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  placeholder="email@example.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 text-sm"
                  autoComplete="email"
                  {...register("email")}
                />
                {errors?.email && (
                  <p className="mt-1 text-xs text-red-600">{errors.email.message}</p>
                )}
                {emailError && (
                  <p className="mt-1 text-xs text-red-600">{emailError}</p>
                )}
              </div>

              {/* Password Field */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Password
                </label>
                <input
                  type="password"
                  placeholder="Create a strong password"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 text-sm"
                  autoComplete="new-password"
                  {...register("password")}
                />
                {errors?.password && (
                  <p className="mt-1 text-xs text-red-600">{errors.password.message}</p>
                )}
              </div>

              {/* Confirm Password Field */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Confirm Password
                </label>
                <input
                  type="password"
                  placeholder="Re-enter your password"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 text-sm"
                  autoComplete="new-password"
                  {...register("confirmPassword")}
                />
                {errors?.confirmPassword && (
                  <p className="mt-1 text-xs text-red-600">{errors.confirmPassword.message}</p>
                )}
              </div>

              {/* Role Selection */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Account Type
                </label>
                <Select
                  isClearable={true}
                  options={rolesList}
                  value={langValue ? rolesList.find(x => x.value === langValue) : langValue}
                  onChange={option => langOnChange(option ? option.value : option)}
                  {...restLangField}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  placeholder="Select account type..."
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      border: '1px solid #d1d5db',
                      borderRadius: '0.5rem',
                      minHeight: '40px',
                      fontSize: '14px',
                      boxShadow: state.isFocused ? '0 0 0 2px rgba(59, 130, 246, 0.1)' : 'none',
                      borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
                      '&:hover': {
                        borderColor: state.isFocused ? '#3b82f6' : '#9ca3af'
                      }
                    }),
                    option: (base, state) => ({
                      ...base,
                      fontSize: '14px',
                      backgroundColor: state.isSelected ? '#3b82f6' : state.isFocused ? '#eff6ff' : 'white',
                      color: state.isSelected ? 'white' : '#374151',
                      '&:active': {
                        backgroundColor: '#3b82f6',
                        color: 'white'
                      }
                    })
                  }}
                />
                {errors?.role && (
                  <p className="mt-1 text-xs text-red-600">{errors.role.message}</p>
                )}
              </div>

              {/* Submit Button */}
              <button 
                type="submit"
                disabled={isLoading}
                className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 disabled:from-green-300 disabled:to-green-400 text-white font-semibold py-2 px-4 rounded-lg transition-all duration-200 transform hover:scale-105 disabled:scale-100 shadow-md flex items-center justify-center text-sm"
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Creating Account...
                  </>
                ) : (
                  "Create Account"
                )}
              </button>
            </form>

            {/* Features Reminder */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-3 border border-green-100">
              <div className="flex items-center justify-center mb-2">
                <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center mr-2">
                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <h3 className="text-sm font-semibold text-gray-800">Full Access Included</h3>
              </div>
              <div className="grid grid-cols-2 gap-1 text-xs text-gray-700">
                <div className="flex items-center">
                  <svg className="w-3 h-3 text-green-500 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Phoneme Level
                </div>
                <div className="flex items-center">
                  <svg className="w-3 h-3 text-green-500 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Sentence Level
                </div>
                <div className="flex items-center">
                  <svg className="w-3 h-3 text-green-500 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Progress Tracking
                </div>
                <div className="flex items-center">
                  <svg className="w-3 h-3 text-green-500 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Statistics
                </div>
              </div>
            </div>

            {/* Privacy Policy */}
            <div className="text-center border-t border-gray-200 pt-3">
              <p className="text-xs text-gray-600">
                By creating an account, you agree to our{' '}
                <Link 
                  className="text-blue-600 hover:text-blue-800 font-medium underline" 
                  target="_blank" 
                  rel="noopener noreferrer"  
                  to="/policy"
                >
                  Privacy Policy
                </Link>
              </p>
            </div>

            {/* Error Display */}
            {apiError && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3">
                <div className="flex items-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="stroke-current flex-shrink-0 h-4 w-4 text-red-600 mr-2"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span className="text-red-700 text-xs font-medium">{apiError}</span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
};

export default Register;